import { doc, writeBatch } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import { getAuthBasicTierLimitedHint, resolveIsBasicLimitedTier } from './basicTierLimits';

export type BasicTierLockableResource = {
  id: string;
  createdAt?: unknown;
  isDisabled?: boolean;
  disabledByBasicTier?: boolean;
  disabledAt?: string | null;
};

let planSyncInFlight: Promise<void> | null = null;
let planLastPersistedSignature = '';
let tagCategorySyncInFlight: Promise<void> | null = null;
let tagCategoryLastPersistedSignature = '';

export function normalizeResourceCreatedAt(value: unknown): number {
  if (!value) return Number.MAX_SAFE_INTEGER;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? Number.MAX_SAFE_INTEGER : value.getTime();
  }

  if (typeof value === 'object' && value !== null) {
    if ('toDate' in value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
      const parsed = (value as { toDate: () => Date }).toDate();
      return Number.isNaN(parsed.getTime()) ? Number.MAX_SAFE_INTEGER : parsed.getTime();
    }
    if ('seconds' in value && typeof (value as { seconds?: number }).seconds === 'number') {
      return (value as { seconds: number }).seconds * 1000;
    }
  }

  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? Number.MAX_SAFE_INTEGER : parsed.getTime();
}

export function sortResourcesByCreation<T extends BasicTierLockableResource>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const dateDiff = normalizeResourceCreatedAt(a.createdAt) - normalizeResourceCreatedAt(b.createdAt);
    if (dateDiff !== 0) return dateDiff;
    return a.id.localeCompare(b.id);
  });
}

export function getBasicTierPrimaryResourceId<T extends BasicTierLockableResource>(items: T[]): string | null {
  if (items.length === 0) return null;
  return sortResourcesByCreation(items)[0]?.id ?? null;
}

export function applyBasicTierResourceState<T extends BasicTierLockableResource>(
  items: T[],
  isLimitedTier: boolean,
): T[] {
  const primaryId = getBasicTierPrimaryResourceId(items);

  return items.map((item) => {
    if (!isLimitedTier) {
      if (item.disabledByBasicTier) {
        return {
          ...item,
          isDisabled: false,
          disabledByBasicTier: false,
          disabledAt: null,
        };
      }
      return item;
    }

    if (item.id === primaryId) {
      return {
        ...item,
        isDisabled: false,
        disabledByBasicTier: false,
        disabledAt: null,
      };
    }

    return {
      ...item,
      isDisabled: true,
      disabledByBasicTier: true,
      disabledAt: item.disabledAt || new Date().toISOString(),
    };
  });
}

export function isBasicTierResourceLocked(item: Pick<BasicTierLockableResource, 'isDisabled' | 'disabledByBasicTier'>): boolean {
  return Boolean(item.isDisabled && item.disabledByBasicTier);
}

export function filterActiveBasicTierResources<T extends BasicTierLockableResource>(items: T[]): T[] {
  return items.filter(item => !isBasicTierResourceLocked(item));
}

async function resolveLimitedTier(isLimitedTierOverride?: boolean): Promise<boolean> {
  if (typeof isLimitedTierOverride === 'boolean') {
    return isLimitedTierOverride;
  }

  const authHint = getAuthBasicTierLimitedHint();
  if (typeof authHint === 'boolean') {
    return authHint;
  }

  const user = getAuth().currentUser;
  if (!user) return false;

  return resolveIsBasicLimitedTier(user.uid);
}

function buildPersistSignature<T extends BasicTierLockableResource>(
  items: T[],
  isLimitedTier: boolean,
  prefix: string,
): string {
  const primaryId = getBasicTierPrimaryResourceId(items);
  const relevant = items
    .map(item => [
      item.id,
      item.isDisabled ? '1' : '0',
      item.disabledByBasicTier ? '1' : '0',
    ].join(':'))
    .sort()
    .join('|');

  return `${prefix}:${isLimitedTier ? 'basic' : 'premium'}:${primaryId ?? 'none'}:${relevant}`;
}

function needsPersistUpdate<T extends BasicTierLockableResource>(original: T, desired: T): boolean {
  return (
    Boolean(original.isDisabled) !== Boolean(desired.isDisabled)
    || Boolean(original.disabledByBasicTier) !== Boolean(desired.disabledByBasicTier)
  );
}

export async function syncBasicTierPlanLock<T extends BasicTierLockableResource>(
  plans: T[],
  isLimitedTierOverride?: boolean,
  userId?: string,
): Promise<T[]> {
  const user = getAuth().currentUser;
  const uid = userId || user?.uid;
  if (!uid) return plans;

  const isLimitedTier = await resolveLimitedTier(isLimitedTierOverride);
  const desiredPlans = applyBasicTierResourceState(plans, isLimitedTier);
  const desiredSignature = buildPersistSignature(desiredPlans, isLimitedTier, 'plans');

  const updates = plans
    .map((plan, index) => ({ original: plan, desired: desiredPlans[index] }))
    .filter(({ original, desired }) => needsPersistUpdate(original, desired));

  if (updates.length === 0 || desiredSignature === planLastPersistedSignature) {
    return desiredPlans;
  }

  const runSync = async () => {
    const batch = writeBatch(db);

    updates.forEach(({ original, desired }) => {
      batch.update(doc(db, `users/${uid}/plans`, original.id), {
        isDisabled: Boolean(desired.isDisabled),
        disabledByBasicTier: Boolean(desired.disabledByBasicTier),
        disabledAt: desired.isDisabled ? (desired.disabledAt || new Date().toISOString()) : null,
        updatedAt: new Date().toISOString(),
      });
    });

    await batch.commit();
    planLastPersistedSignature = desiredSignature;
  };

  try {
    if (!planSyncInFlight) {
      planSyncInFlight = runSync().finally(() => {
        planSyncInFlight = null;
      });
    } else {
      planSyncInFlight = planSyncInFlight.then(runSync);
    }
    await planSyncInFlight;
  } catch (error) {
    console.error('[basicTierResourceLock] Failed to persist plan lock state:', error);
  }

  return desiredPlans;
}

export async function syncBasicTierTagCategoryLock<T extends BasicTierLockableResource>(
  categories: T[],
  isLimitedTierOverride?: boolean,
): Promise<T[]> {
  const user = getAuth().currentUser;
  if (!user) return categories;

  const isLimitedTier = await resolveLimitedTier(isLimitedTierOverride);
  const desiredCategories = applyBasicTierResourceState(categories, isLimitedTier);
  const desiredSignature = buildPersistSignature(desiredCategories, isLimitedTier, 'tagCategories');

  const updates = categories
    .map((category, index) => ({ original: category, desired: desiredCategories[index] }))
    .filter(({ original, desired }) => needsPersistUpdate(original, desired));

  if (updates.length === 0 || desiredSignature === tagCategoryLastPersistedSignature) {
    return desiredCategories;
  }

  const runSync = async () => {
    const batch = writeBatch(db);

    updates.forEach(({ original, desired }) => {
      batch.update(doc(db, 'tagCategories', original.id), {
        isDisabled: Boolean(desired.isDisabled),
        disabledByBasicTier: Boolean(desired.disabledByBasicTier),
        disabledAt: desired.isDisabled ? (desired.disabledAt || new Date().toISOString()) : null,
        updatedAt: new Date().toISOString(),
      });
    });

    await batch.commit();
    tagCategoryLastPersistedSignature = desiredSignature;
  };

  try {
    if (!tagCategorySyncInFlight) {
      tagCategorySyncInFlight = runSync().finally(() => {
        tagCategorySyncInFlight = null;
      });
    } else {
      tagCategorySyncInFlight = tagCategorySyncInFlight.then(runSync);
    }
    await tagCategorySyncInFlight;
  } catch (error) {
    console.error('[basicTierResourceLock] Failed to persist tag category lock state:', error);
  }

  return desiredCategories;
}

export function clearBasicTierPlanLockCache(): void {
  planLastPersistedSignature = '';
}

export function clearBasicTierTagCategoryLockCache(): void {
  tagCategoryLastPersistedSignature = '';
}
